import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNetwork } from '../contexts/NetworkContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    Plus, 
    Trash2, 
    Check, 
    Save, 
    AlertTriangle, 
    ChefHat,
    TrendingUp,
    DollarSign,
    Calendar,
    XCircle,
    RefreshCw,
    Eye,
    Package
} from 'lucide-react';

const DailySalesPage = () => {
    const { api, hasPermission, formatCurrency, canViewPrices } = useAuth();
    const [loading, setLoading] = useState(true);
    const [recipes, setRecipes] = useState([]);
    const [todaySales, setTodaySales] = useState(null);
    const [salesHistory, setSalesHistory] = useState([]);
    
    // Form state
    const [salesItems, setSalesItems] = useState([]);
    const [notes, setNotes] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    
    // Dialog states
    const [stockIssuesDialog, setStockIssuesDialog] = useState(false);
    const [stockIssues, setStockIssues] = useState([]);
    const [overrideReason, setOverrideReason] = useState('');
    const [cancelDialog, setCancelDialog] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelSalesId, setCancelSalesId] = useState(null);
    const [detailDialog, setDetailDialog] = useState(false);
    const [detailSales, setDetailSales] = useState(null);
    const [consumptionDetails, setConsumptionDetails] = useState([]);
    
    // Network status
    const { isOnline, canPerformAction, getOfflineMessage } = useNetwork();
    
    // Permissions
    const canCreate = hasPermission('create_daily_sales');
    const canValidate = hasPermission('validate_daily_sales');
    const canCancel = hasPermission('cancel_daily_sales');
    const canOverride = hasPermission('override_stock_insufficient');
    
    // Check if validation is possible (requires online)
    const canValidateNow = canValidate && isOnline;

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [recipesRes, todayRes, historyRes] = await Promise.all([
                api().get('/recipes'),
                api().get('/daily-sales/today'),
                api().get('/daily-sales?limit=30')
            ]);
            
            setRecipes(recipesRes.data);
            
            if (todayRes.data.status !== 'NONE') {
                setTodaySales(todayRes.data);
                // Populate form from existing data
                if (todayRes.data.items) {
                    setSalesItems(todayRes.data.items.map(item => ({
                        recipe_id: item.recipe_id,
                        quantity_sold: item.quantity_sold
                    })));
                }
                setNotes(todayRes.data.notes || '');
            } else {
                setTodaySales(null);
                setSalesItems([]);
            }
            
            setSalesHistory(historyRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleAddItem = () => {
        setSalesItems([...salesItems, { recipe_id: '', quantity_sold: '' }]);
    };

    const handleRemoveItem = (index) => {
        setSalesItems(salesItems.filter((_, i) => i !== index));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...salesItems];
        newItems[index][field] = field === 'quantity_sold' ? parseInt(value) || '' : value;
        setSalesItems(newItems);
    };

    const calculateEstimatedCost = () => {
        let totalCost = 0;
        let totalRevenue = 0;
        
        salesItems.forEach(item => {
            if (item.recipe_id && item.quantity_sold) {
                const recipe = recipes.find(r => r.id === item.recipe_id);
                if (recipe) {
                    totalCost += (recipe.cost_price_cfa || 0) * item.quantity_sold;
                    totalRevenue += (recipe.sale_price_cfa || 0) * item.quantity_sold;
                }
            }
        });
        
        return { totalCost, totalRevenue, margin: totalRevenue - totalCost };
    };

    const handleSave = async () => {
        const validItems = salesItems.filter(item => item.recipe_id && item.quantity_sold > 0);
        
        if (validItems.length === 0) {
            toast.error('Ajoutez au moins un plat avec une quantite');
            return;
        }
        
        try {
            await api().post('/daily-sales', {
                date: selectedDate,
                items: validItems,
                notes
            });
            
            toast.success('Brouillon enregistre');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleValidate = async (overrideData = null) => {
        if (!todaySales?.id) {
            toast.error('Enregistrez d\'abord les ventes');
            return;
        }
        
        try {
            const response = await api().post(`/daily-sales/${todaySales.id}/validate`, overrideData || {
                override_insufficient: false
            });
            
            if (response.data.status === 'STOCK_INSUFFICIENT') {
                setStockIssues(response.data.issues);
                setStockIssuesDialog(true);
                return;
            }
            
            toast.success('Journee validee avec succes !');
            setStockIssuesDialog(false);
            setOverrideReason('');
            
            // Show consumption details
            if (response.data.consumption_details) {
                setConsumptionDetails(response.data.consumption_details);
                setDetailSales(response.data);
                setDetailDialog(true);
            }
            
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur de validation');
        }
    };

    const handleOverrideValidation = async () => {
        if (!overrideReason.trim()) {
            toast.error('Un motif est requis');
            return;
        }
        
        await handleValidate({
            override_insufficient: true,
            override_reason: overrideReason
        });
    };

    const handleCancelRequest = (salesId) => {
        setCancelSalesId(salesId);
        setCancelReason('');
        setCancelDialog(true);
    };

    const handleCancelConfirm = async () => {
        if (!cancelReason.trim() || cancelReason.length < 5) {
            toast.error('Un motif detaille est requis (min 5 caracteres)');
            return;
        }
        
        try {
            await api().post(`/daily-sales/${cancelSalesId}/cancel`, {
                reason: cancelReason
            });
            
            toast.success('Journee annulee, stock restaure');
            setCancelDialog(false);
            setCancelReason('');
            setCancelSalesId(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleViewDetail = async (sales) => {
        try {
            const response = await api().get(`/daily-sales/${sales.id}`);
            setDetailSales(response.data);
            setConsumptionDetails(response.data.consumption_details || []);
            setDetailDialog(true);
        } catch (error) {
            toast.error('Erreur lors du chargement des details');
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            'DRAFT': 'bg-yellow-100 text-yellow-800 border-yellow-200',
            'VALIDATED': 'bg-green-100 text-green-800 border-green-200',
            'CANCELLED': 'bg-red-100 text-red-800 border-red-200'
        };
        const labels = {
            'DRAFT': 'Brouillon',
            'VALIDATED': 'Valide',
            'CANCELLED': 'Annule'
        };
        return (
            <Badge variant="outline" className={styles[status] || ''}>
                {labels[status] || status}
            </Badge>
        );
    };

    const estimates = calculateEstimatedCost();

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="daily-sales-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Ventes du jour
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Enregistrez les plats vendus et calculez le cout matiere
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-40"
                        disabled={todaySales?.status === 'VALIDATED'}
                    />
                </div>
            </div>

            {/* Status Banner */}
            {todaySales && (
                <Card className={`border-l-4 ${
                    todaySales.status === 'VALIDATED' ? 'border-l-green-500 bg-green-50/50' :
                    todaySales.status === 'CANCELLED' ? 'border-l-red-500 bg-red-50/50' :
                    'border-l-yellow-500 bg-yellow-50/50'
                }`}>
                    <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {getStatusBadge(todaySales.status)}
                            <span className="text-sm text-muted-foreground">
                                {todaySales.status === 'VALIDATED' && todaySales.validated_by_name && (
                                    <>Valide par {todaySales.validated_by_name} le {new Date(todaySales.validated_at).toLocaleString('fr-FR')}</>
                                )}
                                {todaySales.status === 'DRAFT' && (
                                    <>Brouillon en cours - {todaySales.items?.length || 0} plat(s)</>
                                )}
                                {todaySales.status === 'CANCELLED' && (
                                    <>Annule: {todaySales.cancel_reason}</>
                                )}
                            </span>
                        </div>
                        {todaySales.status === 'VALIDATED' && canCancel && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handleCancelRequest(todaySales.id)}
                            >
                                <XCircle className="w-4 h-4 mr-2" />
                                Annuler la journee
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Sales Form */}
                <div className="lg:col-span-2">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="font-display flex items-center gap-2">
                                <ChefHat className="w-5 h-5 text-accent" />
                                Plats vendus
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {todaySales?.status !== 'VALIDATED' && todaySales?.status !== 'CANCELLED' ? (
                                <>
                                    {/* Items List */}
                                    <div className="space-y-3">
                                        {salesItems.map((item, index) => (
                                            <div key={index} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                                                <div className="flex-1">
                                                    <Select
                                                        value={item.recipe_id}
                                                        onValueChange={(v) => handleItemChange(index, 'recipe_id', v)}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Selectionner un plat..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {recipes.map(r => (
                                                                <SelectItem key={r.id} value={r.id}>
                                                                    {r.name} {canViewPrices && `(${r.sale_price_formatted})`}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="w-24">
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        placeholder="Qte"
                                                        value={item.quantity_sold}
                                                        onChange={(e) => handleItemChange(index, 'quantity_sold', e.target.value)}
                                                    />
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-9 w-9 text-destructive"
                                                    onClick={() => handleRemoveItem(index)}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>

                                    {canCreate && (
                                        <Button
                                            variant="outline"
                                            className="w-full"
                                            onClick={handleAddItem}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Ajouter un plat
                                        </Button>
                                    )}

                                    {/* Notes */}
                                    <div className="space-y-2 pt-4 border-t">
                                        <Label>Notes (optionnel)</Label>
                                        <Textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder="Remarques sur la journee..."
                                            rows={2}
                                        />
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-3 pt-4 border-t">
                                        {canCreate && (
                                            <Button
                                                variant="outline"
                                                className="flex-1"
                                                onClick={handleSave}
                                                disabled={salesItems.length === 0}
                                            >
                                                <Save className="w-4 h-4 mr-2" />
                                                Enregistrer
                                            </Button>
                                        )}
                                        {canValidate && todaySales?.id && (
                                            <Button
                                                className="flex-1 bg-accent hover:bg-accent/90"
                                                onClick={() => {
                                                    if (!isOnline) {
                                                        toast.error(getOfflineMessage('VALIDATE_SALES'));
                                                        return;
                                                    }
                                                    handleValidate();
                                                }}
                                                disabled={!todaySales?.id || salesItems.length === 0 || !isOnline}
                                                title={!isOnline ? 'Connexion requise pour valider' : ''}
                                            >
                                                <Check className="w-4 h-4 mr-2" />
                                                {isOnline ? 'Valider la journee' : 'Validation (hors ligne)'}
                                            </Button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                // Display validated/cancelled sales
                                <div className="space-y-4">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Plat</TableHead>
                                                <TableHead className="text-right">Quantite</TableHead>
                                                {canViewPrices && (
                                                    <>
                                                        <TableHead className="text-right">Cout</TableHead>
                                                        <TableHead className="text-right">CA</TableHead>
                                                    </>
                                                )}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {todaySales.items?.map((item, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell className="font-medium">{item.recipe_name}</TableCell>
                                                    <TableCell className="text-right">{item.quantity_sold}</TableCell>
                                                    {canViewPrices && (
                                                        <>
                                                            <TableCell className="text-right font-mono">{item.total_cost_formatted}</TableCell>
                                                            <TableCell className="text-right font-mono text-accent">{item.total_revenue_formatted}</TableCell>
                                                        </>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    
                                    {todaySales.notes && (
                                        <p className="text-sm text-muted-foreground pt-3 border-t">
                                            Note: {todaySales.notes}
                                        </p>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Summary Panel */}
                <div className="space-y-4">
                    {/* Estimated/Actual Totals */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="font-display text-base">
                                {todaySales?.status === 'VALIDATED' ? 'Bilan du jour' : 'Estimation'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {canViewPrices ? (
                                <>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground flex items-center gap-2">
                                            <DollarSign className="w-4 h-4" />
                                            Cout matiere
                                        </span>
                                        <span className="font-mono font-semibold">
                                            {todaySales?.status === 'VALIDATED' 
                                                ? todaySales.total_cost_formatted 
                                                : formatCurrency(estimates.totalCost)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground flex items-center gap-2">
                                            <TrendingUp className="w-4 h-4" />
                                            Chiffre d'affaires
                                        </span>
                                        <span className="font-mono font-semibold text-accent">
                                            {todaySales?.status === 'VALIDATED' 
                                                ? todaySales.total_revenue_formatted 
                                                : formatCurrency(estimates.totalRevenue)}
                                        </span>
                                    </div>
                                    <div className="pt-3 border-t flex items-center justify-between">
                                        <span className="font-medium">Marge</span>
                                        <div className="text-right">
                                            <p className="font-mono font-bold text-green-600">
                                                {todaySales?.status === 'VALIDATED' 
                                                    ? todaySales.total_margin_formatted 
                                                    : formatCurrency(estimates.margin)}
                                            </p>
                                            {todaySales?.status === 'VALIDATED' && (
                                                <p className="text-xs text-muted-foreground">
                                                    {todaySales.margin_percent}%
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                    Donnees financieres non disponibles
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Quick Stats */}
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Total plats</span>
                                <span className="font-bold text-lg">
                                    {todaySales?.status === 'VALIDATED' 
                                        ? todaySales.total_dishes
                                        : salesItems.reduce((sum, item) => sum + (parseInt(item.quantity_sold) || 0), 0)}
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* History Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-display flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-accent" />
                        Historique des journees
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {salesHistory.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">
                            Aucune journee enregistree
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead className="text-right">Plats</TableHead>
                                    {canViewPrices && (
                                        <>
                                            <TableHead className="text-right">Cout</TableHead>
                                            <TableHead className="text-right">CA</TableHead>
                                            <TableHead className="text-right">Marge</TableHead>
                                        </>
                                    )}
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {salesHistory.map(sales => (
                                    <TableRow key={sales.id}>
                                        <TableCell className="font-medium">
                                            {new Date(sales.date).toLocaleDateString('fr-FR', {
                                                weekday: 'short',
                                                day: 'numeric',
                                                month: 'short'
                                            })}
                                        </TableCell>
                                        <TableCell>{getStatusBadge(sales.status)}</TableCell>
                                        <TableCell className="text-right">{sales.total_dishes}</TableCell>
                                        {canViewPrices && (
                                            <>
                                                <TableCell className="text-right font-mono">{sales.total_cost_formatted}</TableCell>
                                                <TableCell className="text-right font-mono text-accent">{sales.total_revenue_formatted}</TableCell>
                                                <TableCell className="text-right">
                                                    <span className="font-mono text-green-600">{sales.total_margin_formatted}</span>
                                                    <span className="text-xs text-muted-foreground ml-1">({sales.margin_percent}%)</span>
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell>
                                            <div className="flex justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => handleViewDetail(sales)}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {sales.status === 'VALIDATED' && canCancel && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-red-500"
                                                        onClick={() => handleCancelRequest(sales.id)}
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Stock Issues Dialog */}
            <Dialog open={stockIssuesDialog} onOpenChange={setStockIssuesDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-yellow-600">
                            <AlertTriangle className="w-5 h-5" />
                            Stock insuffisant
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Certains ingredients n'ont pas assez de stock pour cette production :
                        </p>
                        <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                            {stockIssues.map((issue, idx) => (
                                <div key={idx} className="p-3 flex justify-between items-center">
                                    <div>
                                        <p className="font-medium text-sm">{issue.product_name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            Besoin: {issue.needed.toFixed(2)} {issue.unit}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-red-600">
                                            Dispo: {issue.available.toFixed(2)} {issue.unit}
                                        </p>
                                        <p className="text-xs text-red-500">
                                            Manque: {issue.shortage.toFixed(2)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {canOverride && (
                            <div className="space-y-2 pt-4 border-t">
                                <Label>Motif de la validation forcee *</Label>
                                <Textarea
                                    value={overrideReason}
                                    onChange={(e) => setOverrideReason(e.target.value)}
                                    placeholder="Expliquez pourquoi vous forcez cette validation..."
                                    rows={2}
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setStockIssuesDialog(false)}>
                            Annuler
                        </Button>
                        {canOverride && (
                            <Button 
                                className="bg-yellow-600 hover:bg-yellow-700"
                                onClick={handleOverrideValidation}
                                disabled={!overrideReason.trim()}
                            >
                                <AlertTriangle className="w-4 h-4 mr-2" />
                                Forcer la validation
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel Confirmation Dialog */}
            <AlertDialog open={cancelDialog} onOpenChange={setCancelDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Annuler cette journee ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action va restaurer le stock pour tous les ingredients consommes.
                            Un motif detaille est obligatoire.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-4">
                        <Label>Motif de l'annulation *</Label>
                        <Textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Expliquez la raison de cette annulation..."
                            rows={3}
                            className="mt-2"
                        />
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Non, garder</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleCancelConfirm}
                            className="bg-red-600 hover:bg-red-700"
                            disabled={cancelReason.length < 5}
                        >
                            Oui, annuler et restaurer le stock
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Detail View Dialog */}
            <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            Detail - {detailSales?.date && new Date(detailSales.date).toLocaleDateString('fr-FR', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </DialogTitle>
                    </DialogHeader>
                    {detailSales && (
                        <div className="space-y-6">
                            {/* Status */}
                            <div className="flex items-center gap-3">
                                {getStatusBadge(detailSales.status)}
                                {detailSales.validated_by_name && (
                                    <span className="text-sm text-muted-foreground">
                                        par {detailSales.validated_by_name}
                                    </span>
                                )}
                            </div>

                            {/* Items */}
                            <div>
                                <h4 className="font-semibold mb-2 flex items-center gap-2">
                                    <ChefHat className="w-4 h-4" />
                                    Plats vendus
                                </h4>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Plat</TableHead>
                                            <TableHead className="text-right">Qte</TableHead>
                                            {canViewPrices && (
                                                <>
                                                    <TableHead className="text-right">Cout unit.</TableHead>
                                                    <TableHead className="text-right">Total</TableHead>
                                                </>
                                            )}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {detailSales.items?.map((item, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{item.recipe_name}</TableCell>
                                                <TableCell className="text-right">{item.quantity_sold}</TableCell>
                                                {canViewPrices && (
                                                    <>
                                                        <TableCell className="text-right font-mono">{item.unit_cost_formatted}</TableCell>
                                                        <TableCell className="text-right font-mono">{item.total_cost_formatted}</TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Consumption Details */}
                            {consumptionDetails.length > 0 && (
                                <div>
                                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                                        <Package className="w-4 h-4" />
                                        Consommation ingredients
                                    </h4>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Ingredient</TableHead>
                                                <TableHead className="text-right">Consomme</TableHead>
                                                <TableHead className="text-right">Stock avant</TableHead>
                                                <TableHead className="text-right">Stock apres</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {consumptionDetails.map((item, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{item.product_name}</TableCell>
                                                    <TableCell className="text-right font-mono text-red-600">
                                                        -{item.quantity_consumed.toFixed(2)} {item.unit}
                                                    </TableCell>
                                                    <TableCell className="text-right font-mono text-muted-foreground">
                                                        {item.previous_stock.toFixed(2)}
                                                    </TableCell>
                                                    <TableCell className="text-right font-mono">
                                                        {item.new_stock.toFixed(2)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            {/* Totals */}
                            {canViewPrices && (
                                <div className="pt-4 border-t grid grid-cols-3 gap-4">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Cout matiere</p>
                                        <p className="font-mono font-semibold">{detailSales.total_cost_formatted}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Chiffre d'affaires</p>
                                        <p className="font-mono font-semibold text-accent">{detailSales.total_revenue_formatted}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Marge</p>
                                        <p className="font-mono font-semibold text-green-600">{detailSales.total_margin_formatted}</p>
                                        <p className="text-xs text-muted-foreground">{detailSales.margin_percent}%</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DailySalesPage;
